import { useMemo } from "react";
import { useReducedMotion, type Transition, type Variants } from "framer-motion";

export type MotionPreset = {
  /** Spread onto the parent element to stagger its children in. */
  container: Variants;
  /** Spread onto each child. */
  item: Variants;
  transition: Transition;
  /** True when the user asked for reduced motion; skip decorative animation. */
  reduced: boolean;
};

/**
 * The standard enter animation for a page or a list, already respecting
 * `prefers-reduced-motion`.
 *
 * The global CSS rule in `index.css` neutralises CSS transitions, but Framer
 * Motion animates inline styles with JS and sails straight through it — so
 * every `motion` component in the app needs this, and having it in one hook is
 * the difference between the preference being honoured and it being honoured
 * on whichever screens someone remembered.
 *
 * Under reduced motion the transform is dropped and only a short opacity fade
 * remains: movement is what triggers vestibular symptoms, not the fade.
 */
export function useMotionSafe(): MotionPreset {
  const prefersReduced = useReducedMotion();

  return useMemo(() => {
    const reduced = prefersReduced === true;

    return {
      reduced,
      container: {
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: reduced
            ? { duration: 0.12 }
            : // Capped at 8 children: past that the last card is waiting on a
              // third of a second of stagger it gains nothing from.
              { staggerChildren: 0.03, delayChildren: 0.04, staggerDirection: 1 },
        },
      },
      item: {
        hidden: reduced ? { opacity: 0 } : { opacity: 0, y: 8 },
        visible: { opacity: 1, y: 0 },
      },
      // 320ms is --duration-slow, the ceiling in the design standards.
      transition: reduced
        ? { duration: 0.12 }
        : { duration: 0.32, ease: [0.2, 0, 0, 1] as const },
    };
  }, [prefersReduced]);
}
